import { ConditionalLink } from "components/conditionalLink";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Link } from "components/DesignSystem/Link/Link";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { Info } from "components/icons/Info";
import { SOURCE_SYNC_PAGE_SIZE } from "constants/invoiceSourceSync";
import { REVENUE_DATA_SOURCE_OPTIONS } from "constants/revenueDataSource";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useResyncInvoiceMutation } from "store/apis/invoices";
import { useGetInvoiceSourceSyncQuery } from "store/apis/invoiceSourceSync";
import {
  InvoiceSourceSyncRow,
  SourceSyncChange,
} from "types/Models/invoiceSourceSync";
import { BackendError } from "types/utils/error";
import { formatSourceSyncValue } from "utils/formatSourceSyncValue";
import { sortSourceChanges } from "utils/sortSourceChanges";
import { isSourceSyncInLockPeriod } from "utils/isSourceSyncInLockPeriod";
import { pluralize } from "utils/pluralize";
import { LockedResyncTooltip } from "./LockedResyncTooltip";
import { ResyncInvoiceButton } from "./ResyncInvoiceButton";

type SourceChangesModalProps = {
  open: boolean;
  onClose: () => void;
};

// One row per changed field, most significant first
type ReviewRow = {
  invoice: InvoiceSourceSyncRow;
  change: SourceSyncChange;
  changeIndex: number;
  changeCount: number;
  isLocked: boolean;
};

// Shown once per invoice, spanning all of its change rows
const INVOICE_LEVEL_COLUMNS = new Set(["invoice", "action"]);

const HeaderWithInfo = ({ label, info }: { label: string; info: string }) => (
  <span className="t-flex t-items-center t-gap-1">
    {label}
    <ToolTip text={info} side="top">
      <span className="t-flex t-text-text-30">
        <Info size="14" />
      </span>
    </ToolTip>
  </span>
);

const InvoiceCell = ({ invoice }: { invoice: InvoiceSourceSyncRow }) => {
  const { search } = useLocation();

  return (
    <div className="t-flex t-min-w-0 t-flex-col t-gap-0.5">
      <Link
        to={`/books/invoices-and-customers/${invoice.uuid}${search}`}
        className="t-truncate"
      >
        {invoice.invoice_number || "Untitled"}
      </Link>
      {invoice.customer_name && (
        <span className="t-truncate t-text-body-sm t-text-text-30">
          {invoice.customer_name}
        </span>
      )}
    </div>
  );
};

const ResyncInvoiceAction = ({
  invoice,
  isLocked,
}: {
  invoice: InvoiceSourceSyncRow;
  isLocked: boolean;
}) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [resyncInvoice, { isLoading }] = useResyncInvoiceMutation();
  const blocker: ReactNode = isLocked ? <LockedResyncTooltip /> : null;

  const handleResync = async () => {
    try {
      await resyncInvoice({ entityId, invoiceId: invoice.uuid }).unwrap();
      successToast({ message: `${invoice.invoice_number} re-synced.` });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  return (
    <ResyncInvoiceButton
      blocker={blocker}
      isLoading={isLoading}
      onClick={handleResync}
    />
  );
};

const columnHelper = createColumnHelper<ReviewRow>();

const getColumns = (sourceName: string) => [
  columnHelper.display({
    id: "invoice",
    header: "Invoice",
    size: 22,
    cell: ({ row }) => <InvoiceCell invoice={row.original.invoice} />,
  }),
  columnHelper.display({
    id: "field",
    header: () => (
      <HeaderWithInfo
        label="Field changed"
        info={`The field that differs between ${sourceName} and Inkle`}
      />
    ),
    size: 18,
    cell: ({ row }) => row.original.change.label,
  }),
  columnHelper.display({
    id: "source",
    header: () => (
      <HeaderWithInfo
        label={`On ${sourceName}`}
        info={`The current value in ${sourceName}`}
      />
    ),
    size: 18,
    cell: ({ row }) =>
      formatSourceSyncValue(
        row.original.change.field,
        row.original.change.source,
        row.original.invoice.currency
      ),
  }),
  columnHelper.display({
    id: "inkle",
    header: () => (
      <HeaderWithInfo
        label="On Inkle"
        info="The value Inkle has now; re-sync replaces it"
      />
    ),
    size: 18,
    cell: ({ row }) =>
      formatSourceSyncValue(
        row.original.change.field,
        row.original.change.inkle,
        row.original.invoice.currency
      ),
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    size: 24,
    cell: ({ row }) => (
      <ResyncInvoiceAction
        invoice={row.original.invoice}
        isLocked={row.original.isLocked}
      />
    ),
  }),
];

export const SourceChangesModal = ({
  open,
  onClose,
}: SourceChangesModalProps) => {
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();

  const { data, isLoading, isSuccess, isError } = useGetInvoiceSourceSyncQuery(
    { entityId, limit: SOURCE_SYNC_PAGE_SIZE },
    { skip: !entityId }
  );
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const invoices = data?.invoices ?? [];
  const source = invoices[0]?.source;
  const sourceName =
    (source && REVENUE_DATA_SOURCE_OPTIONS[source]) || "the source";
  const rows: ReviewRow[] = invoices.flatMap((invoice) => {
    const changes = sortSourceChanges(invoice.source_sync);
    const isLocked = isSourceSyncInLockPeriod({
      invoiceDate: invoice.invoice_date,
      sourceSync: invoice.source_sync,
      lockDate: bookkeepingSettings?.accounting_lock_date,
    });
    return changes.map((change, changeIndex) => ({
      invoice,
      change,
      changeIndex,
      changeCount: changes.length,
      isLocked,
    }));
  });
  const hiddenCount = (data?.total_count ?? 0) - invoices.length;

  const columns = useMemo(() => getColumns(sourceName), [sourceName]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1, maxSize: 100 },
  });

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content size="xl">
        <Modal.Header>
          <Modal.Title>Review invoices</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <p className="t-m-0 t-text-body-sm t-text-text-60">
            Compare the data in {sourceName} with Inkle Books and re-sync where
            needed. Locked-period invoices require a lock date change in{" "}
            <ConditionalLink
              target="_blank"
              to={link("/books/books-settings")}
              className="t-text-text-60 !t-underline"
            >
              Settings
            </ConditionalLink>{" "}
            first.
          </p>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={invoices.length === 0}
          >
            <Async.Empty>
              <p className="t-m-0 t-py-6 t-text-center t-text-body-sm t-text-text-30">
                All synced invoices match their source.
              </p>
            </Async.Empty>
            <Async.Success>
              <Table.Container>
                <Table.Content>
                  <Table.Head>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Table.Row key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <Table.HeadCell
                            key={header.id}
                            style={{ width: `${header.getSize()}%` }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Table.HeadCell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Head>
                  <Table.Body>
                    {table.getRowModel().rows.map((row) => (
                      <Table.Row key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          const isInvoiceLevel = INVOICE_LEVEL_COLUMNS.has(
                            cell.column.id
                          );
                          if (isInvoiceLevel && row.original.changeIndex > 0) {
                            return null;
                          }
                          return (
                            <Table.Cell
                              key={cell.id}
                              rowSpan={
                                isInvoiceLevel
                                  ? row.original.changeCount
                                  : undefined
                              }
                              style={{ width: `${cell.column.getSize()}%` }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.Container>
              {hiddenCount > 0 && (
                <span className="t-text-body-sm t-text-text-30">
                  And {pluralize(hiddenCount, "more invoice", "more invoices")}
                </span>
              )}
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
